import { Mediator } from '@studio/design-system/input';
import { Ellipses } from '@studio/design-system/label';
import { motion } from '@studio/design-system/motion';
import type { Identifiable } from '@studio/models';
import { useDoubleClick } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import { useImperativeHandle, useState } from 'react';
import type { ReactElement, Ref } from 'react';
import { isElement } from 'react-is';
import { Card as _Card } from './card.jsx';
import { Footer } from './footer.jsx';
import { Settings } from './settings.jsx';
import type { Behavior, EditableProps } from './types.js';

/** `<Editable>` card scaffold. */
export const Editable = <T extends Identifiable>({
  children,
  forwardedRef,
  handler,
  isSelected,
  item,
  maxLength,
  settings,
  ...props
}: EditableProps<T> & { forwardedRef: Ref<Behavior> }): ReactElement => {
  const [isEditing, setEditing] = useState(false);

  useImperativeHandle(forwardedRef, () => ({
    setEditing
  }));

  const clicks = useDoubleClick({
    onDoubleClick: (): void => {
      if (settings === false) {
        return;
      }

      setEditing(true);
    },

    onSingleClick: (): void => {
      handler.onSelect(item.id);
    }
  });

  return (
    <_Card
      isSelected={isSelected}
      onClick={(): void => {
        handler.onSelect(item.id);
      }}
      {...props}
    >
      <>{children}</>

      <Footer isSelected={isSelected}>
        {isEditing ? (
          <Mediator
            className='h-16 rounded-none px-2'
            focusOnMount
            maxLength={maxLength}
            onCancel={(): void => {
              setEditing(false);
            }}
            onCommit={(nextValue: string): void => {
              const nextName = toString(nextValue);

              if (nextName && nextName !== item.name) {
                handler.onRename(item.id, nextName);
              }

              setEditing(false);
            }}
            selectOnMount
            size='sm'
            value={item.name}
          />
        ) : (
          <>
            <motion.div className='min-w-0 select-none' {...clicks} layout='position'>
              <Ellipses title={item.name}>{item.name}</Ellipses>
            </motion.div>

            {settings !== false &&
              (isElement(settings) ? (
                settings
              ) : (
                <Settings
                  className='w-18'
                  icon={settings ?? 'settings'}
                  onClick={(): void => {
                    handler.onSettings(item.id);
                  }}
                />
              ))}
          </>
        )}
      </Footer>
    </_Card>
  );
};
